import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PrintService } from './print.service';
import { ReportPrintData } from './print-options';

interface jsPDFWithPlugin extends jsPDF {
  autoTable: (options: any) => jsPDF;
  lastAutoTable: { finalY: number };
}

@Injectable({ providedIn: 'root' })
export class ReportPrintService {
  constructor(private printService: PrintService) {}

  printTableReport(data: ReportPrintData): void {
    const doc = this.printService.createDoc(
      data.orientation === 'landscape' ? 'l' : 'p',
      data.unit || 'mm',
      data.format || [210, 297]
    ) as jsPDFWithPlugin;

    const unit = data.unit || 'mm';
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

    if (data.logo) {
      this.printService.addLogo(doc, data.logo, pageWidth - (unit === 'in' ? 1.2 : 30), unit === 'in' ? 0.1 : 5, unit === 'in' ? 0.8 : 20, unit === 'in' ? 0.8 : 20);
    }
    if (data.agency?.logoBase64) {
      this.printService.addLogo(doc, data.agency.logoBase64, unit === 'in' ? 0.2 : 5, unit === 'in' ? 0.1 : 5, unit === 'in' ? 0.8 : 20, unit === 'in' ? 0.8 : 20);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(unit === 'in' ? 16 : 14);
    const titleY = unit === 'in' ? 0.5 : 15;
    doc.text(data.title, pageWidth / 2, titleY, { align: 'center' });
    const titleWidth = doc.getTextWidth(data.title);
    const underlineY = titleY + (unit === 'in' ? 0.05 : 1.5);
    doc.line((pageWidth - titleWidth) / 2, underlineY, (pageWidth + titleWidth) / 2, underlineY);

    let startY = unit === 'in' ? 1.1 : 30;
    const isDailyReport = data.title.toLowerCase().includes('journali');

    if (isDailyReport) {
      let subtitle = "";
      if (data.agency?.name) {
        subtitle += `Agence: ${data.agency.name}`;
      }
      if (data.date) {
        if (subtitle) subtitle += "    |    ";
        subtitle += `Date: ${this.printService.formatDate(data.date)}`;
      }
      if (subtitle) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(subtitle, pageWidth / 2, unit === 'in' ? 0.75 : 22, { align: 'center' });
      }
      startY = unit === 'in' ? 1.3 : 32;
    } else {
      if (data.agency) {
        this.printService.addAgencyHeader(doc, data.agency, unit === 'in' ? 0.3 : 8, unit === 'in' ? 0.7 : 20, unit);
        startY = unit === 'in' ? 1.4 : 38;
      }
    }

    const head = [data.headers];
    const isLargeTable = data.headers && data.headers.length > 10;
    const headerFontSize = isLargeTable ? 7.5 : (unit === 'in' ? 10 : 10);
    const bodyFontSize = isLargeTable ? 7 : (unit === 'in' ? 9 : 9);
    const cellPadding = isLargeTable ? 1.0 : 1.5;

    autoTable(doc, {
      head,
      body: data.rows,
      showHead: 'firstPage',
      startY,
      pageBreak: 'auto',
      rowPageBreak: 'auto',
      theme: 'plain',
      horizontalPageBreak: false,
      headStyles: {
        fontSize: headerFontSize,
        fillColor: '#ffffff',
        fontStyle: 'bold',
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
        cellPadding: cellPadding,
      },
      styles: {
        overflow: 'ellipsize',
        fontSize: bodyFontSize,
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
        textColor: '#000000',
        cellPadding: cellPadding,
      },
    });

    const finalY = doc.lastAutoTable.finalY + 1;
    let nextY = finalY + (unit === 'in' ? 0.4 : 10);

    if (isDailyReport && (data as any).recap) {
      const recap = (data as any).recap;
      const recapHeaders = [
        'Rubrique', 'Détail Quantitatif', 'Total Montant'
      ];
      const recapRows: any[] = [
        ['Passagers (Voyages Réguliers)', `${recap.totalPassengers || 0} pax`, `${(recap.totalPassengerRevenue || 0).toLocaleString()} F`]
      ];

      if ((recap.totalRentals || 0) > 0 || (recap.totalRentalRevenue || 0) > 0) {
        recapRows.push(['Locations de Bus (Rentals)', `${recap.totalRentals || 0} location(s)`, `${(recap.totalRentalRevenue || 0).toLocaleString()} F`]);
      }

      recapRows.push(
        ['Colis (Messagerie / Parcels)', `${recap.totalParcels || 0} colis`, `${(recap.totalParcelRevenue || 0).toLocaleString()} F`],
        ['Bagages (Enregistrés)', `${recap.totalLuggages || 0} bagage(s)`, `${(recap.totalLuggageRevenue || 0).toLocaleString()} F`],
        ['Autres Recettes (Hors-Voyages)', '-', `${(recap.totalOtherRevenue || 0).toLocaleString()} F`],
        ['Dépenses Totales', '-', `${(recap.totalExpenses || 0).toLocaleString()} F`],
        ['Total Recettes Brutes', '-', `${(recap.grandTotalRevenue || 0).toLocaleString()} F`],
        ['Solde Net de l\'Activité', '-', `${(recap.netDayBalance || 0).toLocaleString()} F`]
      );

      autoTable(doc, {
        head: [recapHeaders],
        body: recapRows,
        startY: nextY,
        theme: 'striped',
        styles: {
          fontSize: 9,
          font: 'helvetica',
          textColor: '#000000',
          lineColor: '#cccccc',
          lineWidth: 0.01
        },
        headStyles: {
          fillColor: '#2b2b2b',
          textColor: '#ffffff',
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 50, halign: 'center' },
          2: { cellWidth: 50, halign: 'right' }
        }
      });
      nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);

      // Render Dynamic Other Revenues (Saisie Dynamique des Autres Recettes) if present
      if (data.otherRevenues && data.otherRevenues.length > 0) {
        const otherRevHeaders = ['Type / Catégorie', 'Libellé Recette', 'Montant Total'];
        const otherRevRows = data.otherRevenues.map((item: any) => [
          item.type || 'RECETTE',
          item.label || 'Recette Divers',
          `+${(item.amount || 0).toLocaleString()} FCFA`
        ]);
        autoTable(doc, {
          head: [otherRevHeaders],
          body: otherRevRows,
          startY: nextY,
          theme: 'striped',
          styles: { fontSize: 8.5, font: 'helvetica', textColor: '#000000', lineColor: '#cccccc', lineWidth: 0.01 },
          headStyles: { fillColor: '#0284c7', textColor: '#ffffff', fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 90 }, 2: { cellWidth: 60, halign: 'right' } }
        });
        nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);
      }

      // Render Dynamic Other Expenses (Saisie Manuelle des Autres Dépenses) if present
      if (data.otherExpenses && data.otherExpenses.length > 0) {
        const otherExpHeaders = ['Type / Catégorie', 'Libellé Dépense', 'Montant Total'];
        const otherExpRows = data.otherExpenses.map((item: any) => [
          item.type || 'DEPENSE',
          item.label || 'Dépense Divers',
          `-${(item.amount || 0).toLocaleString()} FCFA`
        ]);
        autoTable(doc, {
          head: [otherExpHeaders],
          body: otherExpRows,
          startY: nextY,
          theme: 'striped',
          styles: { fontSize: 8.5, font: 'helvetica', textColor: '#000000', lineColor: '#cccccc', lineWidth: 0.01 },
          headStyles: { fillColor: '#ef4444', textColor: '#ffffff', fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 90 }, 2: { cellWidth: 60, halign: 'right' } }
        });
        nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);
      }

      // Render Category & Voyage Type Breakdown if present
      if (data.categoryBreakdown && data.categoryBreakdown.length > 0) {
        const catHeaders = ['Catégorie', 'Type Voyage', 'Voyages', 'Passagers', 'Tickets', 'Locations', 'Total Net'];
        const catRows = data.categoryBreakdown.map((cb: any) => [
          cb.categoryName || 'N/A',
          cb.voyageType === 'STANDARD' ? 'Régulier' : (cb.voyageType === 'LANCEMENT' ? 'Lancement' : (cb.voyageType === 'PARCEL_ONLY' || cb.voyageType === 'PARCEL' ? 'Colis Uniq' : (cb.voyageType === 'TRANSPORT_UNIQUE' ? 'Transp. Unique' : 'Location'))),
          (cb.voyageCount || 0).toString(),
          (cb.passengerCount || 0).toString(),
          `${(cb.passengerRevenue || 0).toLocaleString()} F`,
          `${(cb.rentalRevenue || 0).toLocaleString()} F`,
          `${(cb.totalRevenue || 0).toLocaleString()} F`
        ]);
        autoTable(doc, {
          head: [catHeaders],
          body: catRows,
          startY: nextY,
          theme: 'striped',
          styles: { fontSize: 8.5, font: 'helvetica', textColor: '#000000', lineColor: '#cccccc', lineWidth: 0.01 },
          headStyles: { fillColor: '#1a237e', textColor: '#ffffff', fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: 30 },
            2: { cellWidth: 20, halign: 'center' },
            3: { cellWidth: 25, halign: 'center' },
            4: { cellWidth: 30, halign: 'right' },
            5: { cellWidth: 30, halign: 'right' },
            6: { cellWidth: 35, halign: 'right' }
          }
        });
        nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);
      }

      // Render Dynamic Location Types Breakdown if present
      const locBreakdown = data.locationTypeBreakdown || (data.recap && data.recap.locationTypeBreakdown);
      if (locBreakdown && locBreakdown.length > 0) {
        const locHeaders = ['Type de Location', 'Nombre de Locations', 'Montant Contrat', 'Cash Collecté', 'Total Net (FCFA)'];
        const locRows = locBreakdown.map((lt: any) => [
          lt.locationTypeName || 'Location Standard',
          (lt.rentalCount || 0).toString(),
          `${(lt.totalRentalRevenue || 0).toLocaleString()} F`,
          `${(lt.enRouteCashCollected || 0).toLocaleString()} F`,
          `${(lt.grandTotal || 0).toLocaleString()} F`
        ]);
        autoTable(doc, {
          head: [locHeaders],
          body: locRows,
          startY: nextY,
          theme: 'striped',
          styles: { fontSize: 8.5, font: 'helvetica', textColor: '#000000', lineColor: '#cccccc', lineWidth: 0.01 },
          headStyles: { fillColor: '#c2185b', textColor: '#ffffff', fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 65 },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 35, halign: 'right' },
            3: { cellWidth: 35, halign: 'right' },
            4: { cellWidth: 40, halign: 'right' }
          }
        });
        nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);
      }

      const sessionCollections = data.dailySessionCollections || (data.recap && data.recap.dailySessionCollections);
      if (sessionCollections && sessionCollections.length > 0) {
        const sesHeaders = ['N° Session', 'Agent / Guichetier', 'Passagers', 'Colis', 'Bagages', 'Autres Recettes', 'Dépenses', 'Solde Net'];
        const sesRows: any[] = [];
        
        let sumPassagers = 0;
        let sumColis = 0;
        let sumBagages = 0;
        let sumAutres = 0;
        let sumDepenses = 0;

        sessionCollections.forEach((s: any) => {
          const pass = (s.passengerRevenue || s.passengerAmount || 0);
          const col = (s.parcelRevenue || s.parcelAmount || 0);
          const bag = (s.luggageRevenue || s.luggageAmount || 0);
          const autre = (s.otherRevenue || s.autresRecettes || 0);
          const dep = (s.totalExpenses || s.depensesAmount || 0);
          const net = (s.totalAmount || s.netAmount || (pass + col + bag + autre - dep));

          sumPassagers += pass;
          sumColis += col;
          sumBagages += bag;
          sumAutres += autre;
          sumDepenses += dep;

          sesRows.push([
            s.idSession || 'N/A',
            s.createdBy || s.name || 'N/A',
            `${pass.toLocaleString()} F`,
            `${col.toLocaleString()} F`,
            `${bag.toLocaleString()} F`,
            `${autre.toLocaleString()} F`,
            `${dep.toLocaleString()} F`,
            `${net.toLocaleString()} F`
          ]);
        });

        const sumNet = (sumPassagers + sumColis + sumBagages + sumAutres) - sumDepenses;

        // Add final aggregated TOTAL SESSIONS summary row
        sesRows.push([
          'TOTAL SESSIONS',
          `${sessionCollections.length} Session(s)`,
          `${sumPassagers.toLocaleString()} F`,
          `${sumColis.toLocaleString()} F`,
          `${sumBagages.toLocaleString()} F`,
          `${sumAutres.toLocaleString()} F`,
          `${sumDepenses.toLocaleString()} F`,
          `${sumNet.toLocaleString()} F`
        ]);

        autoTable(doc, {
          head: [sesHeaders],
          body: sesRows,
          startY: nextY,
          theme: 'striped',
          styles: { fontSize: 8.5, font: 'helvetica', textColor: '#000000', lineColor: '#cccccc', lineWidth: 0.01 },
          headStyles: { fillColor: '#1a237e', textColor: '#ffffff', fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 45 },
            2: { cellWidth: 26, halign: 'right' },
            3: { cellWidth: 26, halign: 'right' },
            4: { cellWidth: 26, halign: 'right' },
            5: { cellWidth: 26, halign: 'right' },
            6: { cellWidth: 26, halign: 'right' },
            7: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }
          }
        });
        nextY = doc.lastAutoTable.finalY + (unit === 'in' ? 0.4 : 10);
      }
    }

    if (!isDailyReport && data.date) {
      doc.setFontSize(unit === 'in' ? 10 : 10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${this.printService.formatDate(data.date)}`, pageWidth / 2, finalY, { align: 'center' });
    }

    const signatures = isDailyReport ? ["Visa de l'Agent", "Visa du Chef"] : undefined;
    this.printService.addSignatureFields(doc, nextY + (unit === 'in' ? 0.3 : 6), unit, signatures);
    this.printService.outputPDF(doc, (data as any).output);
  }

  printSessionReport(
    datas: any,
    collis: any[],
    base64?: string,
  ): void {
    const data = collis;
    let heightdoc = 0.3 * (data?.length - 2);
    if (collis?.length < 4) {
      heightdoc = 0;
    }
    const doc = new jsPDF(
      collis?.length > 23 ? 'p' : 'l',
      'in',
      [3 + heightdoc, 8.25],
    ) as jsPDFWithPlugin;

    const content: any[] = [];
    for (let i = 0; i < data.length; i++) {
      content.push([
        i + 1,
        data[i].ticket,
        data[i].descriptioncollis,
        data[i].sender.name,
        data[i].receiver[0].name,
        data[i].receiver[0].primarynumber,
        data[i].tarif,
        this.formatDate(data[i].creationdate),
      ]);
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERAL EXPRESS VOYAGES', 3, 0.27);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(
      'RAPPORT DE SESSION #' + datas.idsessiion + '/' + datas.sessionCount,
      0.3,
      0.4,
    );
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Agent:  ' + datas.name, 0.6, 0.6);
    doc.text('S/N:  ' + datas.idsessiion, 0.6, 0.8);
    doc.text('Montant:  ' + datas.sessionAmount + ' FCFA', 0.6, 1);
    doc.text(
      'Date Ouverture S/N:  ' + this.formatDate(datas.creationDate),
      3,
      0.8,
    );
    doc.text(
      'Date Fermeture S/N:  ' + this.formatDate(datas.closingDate),
      3,
      1,
    );
    doc.text('Date:  ' + this.formatDate(new Date()), 3, 0.6);

    if (base64) {
      doc.addImage(base64, 'JPEG', 6.7, 0, 1, 1);
    }

    autoTable(doc, {
      head: [
        [
          '#',
          'N°',
          'Nature du Colis',
          'Expéditeur',
          'Destinataire',
          'Telephone',
          'Prix',
          'Date',
        ],
      ],
      body: content,
      showHead: 'firstPage',
      startY: 1.1,
      pageBreak: 'auto',
      rowPageBreak: 'auto',
      theme: 'plain',
      horizontalPageBreak: false,
      headStyles: {
        fontSize: 12,
        fillColor: '#ffffff',
        fontStyle: 'bold',
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
      },
      styles: {
        overflow: 'ellipsize',
        fontSize: 10,
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
        textColor: '#000000',
      },
      didDrawPage: (tableData: any) => {
        doc.setFontSize(11);
        const pageCount = (doc as any).internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        if ((doc as any).internal.getCurrentPageInfo().pageNumber == pageCount) {
          doc.text(
            'TOTAL MONTANT: ' + datas.sessionAmount + ' FCFA',
            tableData.settings.margin.right + 2,
            pageHeight - 0.7,
            { align: 'center' },
          );
          doc.setFontSize(10);
          doc.text(
            'Visa Agent',
            tableData.settings.margin.right + 2,
            pageHeight - 0.5,
            { align: 'center' },
          );
          doc.text(
            datas.name,
            tableData.settings.margin.right + 2,
            pageHeight - 0.2,
            { align: 'center' },
          );
          doc.text(
            'Visa du Chef',
            tableData.settings.margin.right + 4.2,
            pageHeight - 0.5,
            { align: 'center' },
          );
        }
      },
    });

    this.printService.outputPDF(doc, { method: 'printjs' });
  }

  printFiscalReport(
    voyageinfo: any,
    passagers: any[],
    agencedepart: string,
    base64?: string,
  ): void {
    const doc = new jsPDF('p', 'in', 'A4') as jsPDFWithPlugin;

    const content: any[] = [];
    for (let i = 0; i < passagers.length; i++) {
      content.push([
        i + 1,
        passagers[i].ticket2Display || passagers[i].idpvDisplay || passagers[i].idpv,
        passagers[i].place,
        passagers[i].passager.nompassager,
        passagers[i].passager.contact,
        passagers[i].passager.cni,
        passagers[i].destination.nomdest,
        passagers[i].montant,
      ]);
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERAL EXPRESS VOYAGES', 2.6, 0.27);
    doc.setFontSize(9);
    doc.text(
      'Siège Social : B.P. 446 Mbouda - Direction Générale: BP 487 Bafoussam',
      2,
      0.45,
    );
    doc.setFontSize(7);
    doc.text('NOS AGENCES ', 2, 0.7);
    doc.text('Ouest: Bafoussam - Mbouda - Dschang', 2.9, 0.6);
    doc.text(
      'Littoral: Bépanda - Bonabéri - Brazzaville - Mboppi',
      2.9,
      0.7,
    );
    doc.text(
      'Centre: Biyem Assi - Mvan - Carriere - Terminus - Tongolo - Olembé',
      2.9,
      0.8,
    );
    doc.setLineWidth(0.02);
    doc.line(1, 0.9, 7.3, 0.9);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('BORDEREAU DU VOYAGE', 2.9, 1.15);
    doc.setFontSize(7);
    doc.text(
      'Date:  ' + this.formatDate(voyageinfo.voyage.date),
      0.6,
      1.3,
    );
    doc.text(
      'Categorie:  ' + voyageinfo.voyage.category.nomcategory,
      0.6,
      1.42,
    );
    doc.text(
      'Trajet:  ' + agencedepart + ' / ' + voyageinfo.voyage.destination[0].ville.nomville,
      0.6,
      1.54,
    );
    doc.text(
      'N° BORDEREAU:' + (voyageinfo.voyage.idvoyageDisplay || voyageinfo.voyage.idvoyage),
      3.1,
      1.3,
    );
    doc.text('Vehicule:  ' + voyageinfo.bus, 5, 1.3);
    doc.text('Chauffeur:  ' + voyageinfo.chauffeur, 5, 1.42);
    doc.text('Motoboy:  ' + voyageinfo.motoboy, 5, 1.54);

    if (base64) {
      doc.addImage(base64, 'JPEG', 0.2, 0, 1, 1);
      doc.addImage(base64, 'JPEG', 6.7, 0, 1, 1);
    }

    autoTable(doc, {
      head: [
        [
          '#',
          'N° TICKET',
          'SIEGE',
          'PASSAGER',
          'TELEPHONE',
          'CNI',
          'DESTINATION',
          'TARIF',
        ],
      ],
      body: content,
      showHead: 'firstPage',
      startY: 1.6,
      pageBreak: 'auto',
      rowPageBreak: 'auto',
      theme: 'plain',
      tableLineWidth: 0.01,
      horizontalPageBreak: false,
      headStyles: {
        fontSize: 7,
        fillColor: '#ffffff',
        fontStyle: 'bold',
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
      },
      styles: {
        fillColor: '#ffffff',
        overflow: 'ellipsize',
        fontSize: 7,
        font: 'helvetica',
        lineWidth: 0.01,
        lineColor: '#000000',
        textColor: '#000000',
      },
      didDrawPage: () => {
        doc.setTextColor(40);
        doc.setFontSize(7);
      },
    });

    const pageHeights3 = doc.lastAutoTable.finalY + 1;
    doc.setFontSize(7);
    doc.text(
      'Total: ' + voyageinfo.voyage.totalamount + ' XAF',
      2,
      pageHeights3 - 0.9,
      { align: 'center' },
    );
    doc.text(
      'Nombre total passagers: ' + voyageinfo.voyage.countpassager,
      4,
      pageHeights3 - 0.9,
      { align: 'center' },
    );
    doc.text('Signature du Chauffeur ', 2, pageHeights3 - 0.7, {
      align: 'center',
    });
    doc.text("Signature du chef d'Agence ", 6, pageHeights3 - 0.7, {
      align: 'center',
    });
    doc.setLineWidth(0.02);
    doc.line(0.5, pageHeights3 - 0.25, 7.7, pageHeights3 - 0.25);
    doc.text(
      'N° de contribuable: P116800062838 N° Registre de Commerce: 110705252',
      4,
      pageHeights3 - 0.1,
      { align: 'center' },
    );

    this.printService.outputPDF(doc, { method: 'printjs' });
  }

  
  printFidelityTickets(searchQuery: string, searchResults: any[], date: string): void {
    const doc = new jsPDF('l', 'mm', 'a4') as jsPDFWithPlugin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('GENERAL EXPRESS VOYAGES', 148, 15, { align: 'center' });

    doc.setFontSize(12);
    doc.text('Liste des Tickets de Fidélité', 148, 23, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Recherche: ${searchQuery}`, 15, 33);
    doc.text(`Date: ${date}`, 15, 39);
    doc.text(`Total: ${searchResults.length} ticket(s)`, 220, 33);

    const tableData: any[] = [];
    let totalAmount = 0;

    searchResults.forEach((ticket, index) => {
      const dateVoyage = ticket.voyage.datevoyage || ticket.voyage.date;
      tableData.push([
        index + 1,
        ticket.ticket2Display || ticket.idpvDisplay || ticket.idpv,
        ticket.passager?.nompassager || '-',
        ticket.passager?.contact || '-',
        ticket.destination?.nomdest || '-',
        this.formatDate(dateVoyage),
        ticket.place,
        ticket.montant + ' FCFA'
      ]);
      totalAmount += parseFloat(ticket.montant) || 0;
    });

    tableData.push(['', '', '', '', '', '', 'TOTAL:', totalAmount.toLocaleString() + ' FCFA']);

    autoTable(doc, {
      head: [['N°', 'Ticket', 'Passager', 'Téléphone', 'Destination', 'Date', 'Place', 'Montant']],
      body: tableData,
      startY: 45,
      theme: 'grid',
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'center', cellWidth: 30 },
        2: { halign: 'left', cellWidth: 50 },
        3: { halign: 'center', cellWidth: 30 },
        4: { halign: 'center', cellWidth: 40 },
        5: { halign: 'center', cellWidth: 30 },
        6: { halign: 'center', cellWidth: 15 },
        7: { halign: 'right', cellWidth: 30 }
      }
    });

    this.printService.outputPDF(doc, { method: 'printjs' });
  }

  printRemboursementReport(voyagedata: any, remboursement: any[], base64?: string): void {
    const doc = new jsPDF('p', 'in', 'a4') as jsPDFWithPlugin;

    const content: any = [];
    for (let i = 0; i < remboursement.length; i++){
      content.push([
        (i+1),
        remboursement[i].place,
        remboursement[i].passager.nompassager,
        remboursement[i].reste
      ]);
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('FICHE DE REMBOURSEMENT', 4.135, 0.5, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Date:  ' + this.formatDate(new Date()) , 4.135, 0.7, { align: 'center' });

    const agenceStr = localStorage.getItem('agenceinso');
    const agenceName = agenceStr ? JSON.parse(agenceStr).nomagence : '';
    let trajet = voyagedata?.nomvoyage || '';
    if (trajet.includes('-')) {
      trajet = trajet.replace(/\s*-\s*/, ' ---> ');
    } else {
      const destName = voyagedata?.destination?.[0]?.ville?.nomville || trajet;
      trajet = agenceName && destName ? `${agenceName} ---> ${destName}` : destName;
    }

    doc.text('Trajet:  ' + trajet, 0.5, 1.2);
    doc.text('Vehicule:  '   +  (voyagedata?.busname || '-'), 0.5, 1.4);
    doc.text('Chauffeur:  ' + (voyagedata?.chauffeur?.nomchauffeur || '-'), 3.0, 1.4);

    if (base64) {
      doc.addImage(base64, 'JPEG', 0.5, 0.2, 1.2, 0.8);
      doc.addImage(base64, 'JPEG', 6.5, 0.2, 1.2, 0.8);
    }

    autoTable(doc, {
      head: [['#', 'Place', 'PASSAGER', 'RELIQUAT']],
      body: content,
      showHead: 'firstPage',
      startY: 1.8,
      pageBreak: 'auto',
      rowPageBreak: 'auto',
      theme: 'grid',
      headStyles: { fillColor: '#ffffff', textColor: '#000000', lineWidth: 0.01, lineColor: '#000000' },
      styles: { fontSize: 10, cellPadding: 0.1, textColor: '#000000', lineColor: '#000000', lineWidth: 0.01 }
    });

    const pageHeights3 = doc.lastAutoTable.finalY + 0.5;
    doc.setFontSize(10);
    const totalAmount = remboursement.reduce((sum, item) => sum + (parseFloat(item.reste) || 0), 0);
    doc.text('Total: ' + totalAmount + ' XAF', 7.5, pageHeights3, { align: 'right' });
    doc.text('Visa Agent', 1.0, pageHeights3 + 0.5);
    doc.text('Visa du client', 6.0, pageHeights3 + 0.5);

    this.printService.outputPDF(doc, { method: 'printjs' });
  }

  printPassengerSessionSummary(
    response: any[],
    sessiondetail: any,
    name: string,
    base64: string,
    onClose?: () => void
  ): void {
    let doc: any = new jsPDF("l", "in", [3, 3.5], true) as jsPDFWithPlugin;

    const contentticket: any = [];
    let totalpercu = 0;
    for (let i = 0; i < response.length; i++) {
      const rowdata = [];
      for (let index = 0; index < response.length; index++) {
        rowdata.push(i + 1, response[i][0], response[i][2], response[i][1]);
        totalpercu = Number(totalpercu) + Number(response[i][1]);
      }
      contentticket.push(rowdata);
    }

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("GENERAL EXPRESS VOYAGES", 0.9, 0.2);
    doc.setFontSize(8);
    doc.text("RAPPORT DE FIN DE SESSION", 1.1, 0.35);

    doc.setFontSize(7);
    doc.text("Date d'impression:  " + this.formatDate(new Date()), .7, 0.55);
    doc.text("Date D'Ouverture S/N: " + this.formatDate(sessiondetail?.creationDate), 0.7, 0.65);
    doc.text("Date De Fermeture S/N: " + this.formatDate(sessiondetail?.closingDate), 0.7, 0.75);
    doc.text("Agent: " + name, 0.7, 0.85);
    doc.text("Visa ", 1.5, 2.8);

    if (base64) {
      doc.addImage(base64, "JPEG", 0.05, 0.08, 0.5, 0.6, "", "FAST");
    }

    doc.setFont("helvetica", "bold");
    autoTable(doc, {
      head: [["N°", "Tarif", "Passager", "Total"]],
      body: contentticket,
      showHead: "firstPage",
      startY: 1,
      pageBreak: "avoid",
      rowPageBreak: "avoid",
      theme: 'plain',
      headStyles: {
        fontSize: 9,
        fillColor: "#ffffff",
        fontStyle: "bold",
        font: "helvetica",
        halign: "center",
        lineWidth: 0.01,
        lineColor: "#000000",
      },
      styles: {
        overflow: "ellipsize",
        fontSize: 8,
        font: "helvetica",
        lineWidth: 0.01,
        halign: "center",
        lineColor: "#000000",
        textColor: "#000000",
      },
      columnStyles: {
        0: { overflow: "ellipsize", cellWidth: .4 },
        1: { overflow: "ellipsize", cellWidth: 0.7 },
        2: { overflow: "ellipsize", cellWidth: 0.7 },
        3: { overflow: "ellipsize", cellWidth: 0.7 },
      }
    });

    const pageHeights3 = doc.lastAutoTable.finalY + 1;
    doc.setFontSize(11);
    doc.text("Total: " + (totalpercu / response.length) + ' FCFA', 2, (pageHeights3 - 0.7), {align: 'center'});

    this.printService.outputPDF(doc, { method: 'printjs', onClose });
  }

  printCollisSessionSummary(
    response: any[],
    bagages: any[],
    recette: any[],
    depense: any[],
    sessiondetail: any,
    name: string,
    base64: string,
    onClose?: () => void
  ): void {
    let doc: any = new jsPDF("l", "in", [6, 8.5], true) as jsPDFWithPlugin;

    const contentticket: any = [];
    let totalpercu = 0;
    for (let i = 0; i < response.length; i++) {
      const rowdata = [];
      for (let index = 0; index < response.length; index++) {
        rowdata.push(i + 1, response[i][0], response[i][2], response[i][1]);
        totalpercu = Number(totalpercu) + Number(response[i][1]);
      }
      contentticket.push(rowdata);
    }

    const contentbagage: any = [];
    let totalbagage = 0;
    for (let i = 0; i < bagages.length; i++) {
      const rowdata = [];
      for (let index = 0; index < bagages.length; index++) {
        rowdata.push(i + 1, bagages[i][0], bagages[i][2], bagages[i][1]);
        totalbagage = Number(totalbagage) + Number(bagages[i][1]);
      }
      contentbagage.push(rowdata);
    }

    const contentrecette: any = [];
    let totalrecette = 0;
    for (let i = 0; i < recette.length; i++) {
      const rowdata = [];
      for (let index = 0; index < recette.length; index++) {
        rowdata.push(i + 1, recette[i][0], recette[i][2], recette[i][1]);
        totalrecette = Number(totalrecette) + Number(recette[i][1]);
      }
      contentrecette.push(rowdata);
    }

    const contentdepense: any = [];
    let totaldepense = 0;
    for (let i = 0; i < depense.length; i++) {
      const rowdata = [];
      for (let index = 0; index < depense.length; index++) {
        rowdata.push(i + 1, depense[i][0], depense[i][2], depense[i][1]);
        totaldepense = Number(totaldepense) + Number(depense[i][1]);
      }
      contentdepense.push(rowdata);
    }

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("GENERAL EXPRESS VOYAGES", 2.5, 0.27);
    doc.setFontSize(11);
    doc.text("RAPPORT DE FIN DE SESSION" ,2.7, 0.5 );
    doc.setFontSize(9);
    doc.text("Agent:  " + name, 1.5, 0.65);
    doc.text("S/N:  " + sessiondetail.idsessiion + "  N° " + sessiondetail.sessionCount, 1.5, 0.83);
    doc.text("Montant Total:  " + sessiondetail.sessionAmount + " FCFA", 1.5, 1);
    doc.text("Date d'impression:  " + this.formatDate(new Date()), 5, 0.65);
    doc.text("Date D'Ouverture S/N:  " + this.formatDate(sessiondetail.creationDate), 5, 0.83);
    doc.text("Date De Fermeture S/N:  " + this.formatDate(sessiondetail.closingDate), 5, 1);

    if (base64) {
      doc.addImage(base64, "JPEG", 0.1, 0, 1, 1);
    }

    doc.setFont("helvetica", "bold");
    doc.text("COLIS" ,1.5, 1.3 );

    autoTable(doc, {
      head: [["N°", "Tarif", "Colis", "Total"]],
      body: contentticket,
      showHead: "firstPage",
      startY: 1.4,
      theme: 'plain',
      horizontalPageBreak: false,
      pageBreak: "auto",
      rowPageBreak: "auto",
      headStyles: {
        fontSize: 9,
        fillColor: "#ffffff",
        fontStyle: "bold",
        font: "helvetica",
        halign: "center",
        lineWidth: 0.01,
        lineColor: "#000000",
      },
      styles: {
        overflow: "ellipsize",
        fontSize: 8,
        font: "helvetica",
        lineWidth: 0.01,
        halign: "center",
        lineColor: "#000000",
        textColor: "#000000",
      }
    });
    
    let pageHeights1 = doc.lastAutoTable.finalY + 1;
    doc.setFontSize(11);
    doc.text( "Total Colis: "+ (totalpercu/response.length) + ' FCFA', 5.5, (pageHeights1 - 0.7), {align: 'center'});

    let pageHeights2 = pageHeights1;
    if(bagages.length > 0){
      doc.text("BAGAGE" ,1.5, (pageHeights1 - 0.5));
      autoTable(doc, {
        head: [["N°", "Tarif", "Bagage", "Total"]],
        body: contentbagage,
        showHead: "firstPage",
        startY: (pageHeights1 - 0.4),
        pageBreak: "auto",
        theme: 'plain',
        rowPageBreak: "auto",
        headStyles: { fontSize: 9, fillColor: "#ffffff", fontStyle: "bold", font: "helvetica", halign: "center", lineWidth: 0.01, lineColor: "#000000" },
        styles: { overflow: "ellipsize", fontSize: 8, font: "helvetica", lineWidth: 0.01, halign: "center", lineColor: "#000000", textColor: "#000000" }
      });
      pageHeights2 = doc.lastAutoTable.finalY + 1;
      doc.setFontSize(11);
      doc.text( "Total Bagage: "+ (totalbagage/bagages.length) + ' FCFA', 5.5, (pageHeights2 - 0.7), {align: 'center'});
    }

    let pageHeights3 = pageHeights2;
    if(recette.length > 0){
      doc.text("AUTRE RECETTE" ,1.5, (pageHeights2 - 0.5) );
      autoTable(doc, {
        head: [["N°", "Tarif", "Autre Recette", "Total"]],
        body: contentrecette,
        showHead: "firstPage",
        startY: (pageHeights2 - 0.4) ,
        pageBreak: "auto",
        theme: 'plain',
        rowPageBreak: "auto",
        headStyles: { fontSize: 9, fillColor: "#ffffff", fontStyle: "bold", font: "helvetica", halign: "center", lineWidth: 0.01, lineColor: "#000000" },
        styles: { overflow: "ellipsize", fontSize: 8, font: "helvetica", lineWidth: 0.01, halign: "center", lineColor: "#000000", textColor: "#000000" }
      });
      pageHeights3 = doc.lastAutoTable.finalY + 1;
      doc.setFontSize(11);
      doc.text( "Total Autre Recette: "+ (totalrecette/recette.length) + ' FCFA', 5.5, (pageHeights3 - 0.7), {align: 'center'});
    }

    let pageHeights4 = pageHeights3;
    if(depense.length > 0) {
      doc.text("DEPENSE" ,1.5, (pageHeights3 - 0.5) );
      autoTable(doc, {
        head: [["N°", "Tarif", "Dépense", "Total"]],
        body: contentdepense,
        showHead: "firstPage",
        startY: (pageHeights3 - 0.4) ,
        pageBreak: "auto",
        theme: 'plain',
        rowPageBreak: "auto",
        headStyles: { fontSize: 9, fillColor: "#ffffff", fontStyle: "bold", font: "helvetica", halign: "center", lineWidth: 0.01, lineColor: "#000000" },
        styles: { overflow: "ellipsize", fontSize: 8, font: "helvetica", lineWidth: 0.01, halign: "center", lineColor: "#000000", textColor: "#000000" }
      });
      pageHeights4 = doc.lastAutoTable.finalY + 1;
      doc.setFontSize(11);
      doc.text( "Total Dépense: "+ (totaldepense/depense.length) + ' FCFA', 5.5, (pageHeights4 - 0.7), {align: 'center'});
    }

    const finalPageHeights = doc.lastAutoTable.finalY + 1;
    let computedMontant = (totalpercu/response.length) + (bagages.length > 0 ? (totalbagage/bagages.length) : 0) + (recette.length > 0 ? (totalrecette/recette.length) : 0) + (depense.length > 0 ? (totaldepense/depense.length) : 0);
    doc.text( "Montant Total: "+ computedMontant + ' FCFA', 2, (finalPageHeights - 0.55), {align: 'center'});
    doc.setFontSize(11);
    doc.text('Visa de l\'Agent', 2, (finalPageHeights - 0.35), {align: 'center'});
    doc.text(name, 2, (finalPageHeights - 0.2), {align: 'center'});
    doc.text('Visa du Chef', 4.2, (finalPageHeights - 0.35), {align: 'center'});

    this.printService.outputPDF(doc, { method: 'printjs', onClose });
  }


  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
}
